import { NavigationExtras, Params, ResolveEnd, Router } from '@angular/router';
import { inject, Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import * as RouterActions from '@core/router/store/router.actions';
import { filter, map, tap } from 'rxjs';
import { Location } from '@angular/common';
import { cloneDeep } from 'lodash-es';
import { NgRxEffectsBase } from '@shared/utility/NgRxUtils';
import { getFromStorage } from '@shared/utility/session-local-storage';
import { StorageKeysEnum } from '@shared/utility/storage-keys';
import { getNavigationTree, getRouterHistory, pushRouterHistory } from '@core/router/state/router-navigation-state';

@Injectable({ providedIn: 'any' })
export class RouterEffects extends NgRxEffectsBase {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly embedded = getFromStorage<boolean | undefined>('sessionStorage', StorageKeysEnum.embedded);

  public goEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.go),
        map(({ path, extras }) => {
          let newextras: NavigationExtras = { ...extras };
          if (extras?.queryParams != null) {
            newextras = cloneDeep(extras);
            newextras.queryParams = { ...extras.queryParams };
          }
          if (extras?.state?.['forceReload']) {
            newextras.onSameUrlNavigation = 'reload';
          }
          const embedded = this.embedded();
          if (embedded) {
            if (newextras.queryParams != null) {
              newextras.queryParams = { ...newextras.queryParams, embedded };
            } else {
              newextras.queryParams = { embedded };
            }
          }
          return { path, extras: newextras };
        }),
        tap(({ path, extras }) => {
          void this.router.navigate(path, { ...extras });
        })
      ),
    { dispatch: false }
  );

  public goTreeEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.goTree),
        tap(() => {
          const tree = getNavigationTree();
          if (!tree) {
            return;
          }
          void this.router.navigateByUrl(tree);
        })
      ),
    { dispatch: false }
  );

  public backEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.back),
        tap(() => {
          const history = getRouterHistory();
          if (history.length > 1) {
            history.pop();
            const { path, extras } = history[history.length - 1];
            this.store.dispatch(RouterActions.go({ path, extras: { ...extras } }));
          }
        })
      ),
    { dispatch: false }
  );

  public forwardEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.forward),
        tap(() => {
          this.location.forward();
        })
      ),
    { dispatch: false }
  );

  constructor() {
    super();
    this.router.events.pipe(filter((event) => event instanceof ResolveEnd)).subscribe((e) => {
      const { path, queryParams } = this.resolveUrl(e.url);
      pushRouterHistory({ path, extras: { queryParams } });
    });
  }

  private resolveUrl(url: string): { path: string[]; queryParams: Params } {
    const queryParams = this.router.parseUrl(url).queryParams;
    const path = url
      .split('?')[0]
      .split('/')
      .filter((r) => r);
    return { path, queryParams };
  }
}
